/**
 * <apple-wallet-badge> — Apple's own "Add to Apple Wallet" badge, ONE
 * CONTROL: at order level it addresses the whole order's `.pkpasses` bundle
 * (walletAppleBundleUrl, order-access.state.ts), and with `ticketId` set it
 * addresses that one ticket's own `.pkpass` (walletAppleUrls). A `.pkpasses`
 * file is a ZIP of the individually signed passes served as
 * application/vnd.apple.pkpasses, which iOS 15+ adds in a single add — so the
 * order-level surface offers one Apple control beside Google's one, never a
 * row that grows a badge per ticket.
 *
 * An order fulfilled before that bundle was written carries no bundle path at
 * all, and its individually stored passes are the only thing addressable, so
 * order level falls back to one control per pass for those orders rather than
 * losing the action outright.
 *
 * Both order-level shapes are held inside one ceiling — walletBulkOffered,
 * the lower of the two vendors' own maxima (order-access.state.ts's
 * WALLET_BULK_PASS_CEILING carries the reckoning). Above it this mode renders
 * nothing at all: the bundle is the shape a large order most needs and the
 * shape Apple stops supplying first, and the per-pass fallback there would be
 * precisely the one-badge-per-ticket row the ceiling exists against. The line
 * naming the per-ticket route renders beside the row instead
 * (order-access.view.ts), and each ticket's own card keeps its own pass.
 *
 * Apple forbids recreating this asset — "Do not create your own version of
 * the Add to Apple Wallet badge" — so the mark renders only Apple's own
 * supplied SVG, unaltered, never a fabricated look-alike. That kit is gated
 * behind a developer-site click-through; the path below is where it lands,
 * tracked under web/public/wallet/ beside Google's own committed
 * artwork, in the same per-locale shape. Where it doesn't load, `@error`
 * flips the shared `appleBadgeAssetBroken` signal below and every badge swaps
 * to a plain text link at the same href — the prohibition covers drawing
 * Apple's mark, not naming the action in words (the same plain-sentence class
 * ticket-email.template.ts delivers the attached pass with), and a signed
 * pass the buyer paid for downloads with or without an image.
 *
 * That verdict is Apple's own and reaches nothing else:
 * google-wallet-badge.view.ts carries its own per-element verdict over its own
 * artwork, so neither vendor's missing file can silence the other's badge.
 *
 * Renders only where a `.pkpass` has a functional target: the operating
 * system is what opens one, and only Apple's own do — iOS, iPadOS and macOS,
 * in any browser on them — so on Android, Windows or Linux the file downloads
 * and nothing can act on it, and a badge that does nothing is worse than an
 * absent one. That gate is the one thing this badge does that
 * google-wallet-badge.view.ts doesn't, and the asymmetry is the artifact's,
 * not the vendor's: a Google save link is a web page every platform can open,
 * so Google's badge needs no equivalent and has none.
 *
 * Three states, from the slot itself (order-access.state.ts's
 * walletAppleSlot): `never` renders nothing and reserves nothing, `pending`
 * holds the mark's exact box with a skeleton in it, `ready` carries the mark
 * in the footprint that box already had. The asset itself is never dimmed —
 * that would be altering it; the skeleton stands where the artwork will be
 * rather than being a lightened copy of it. `never` covers two independent
 * answers, and both must reserve nothing: a device whose OS cannot open a
 * `.pkpass`, and a deployment that signs none.
 *
 * `pending` holds that box against the one answer available while a pass is
 * still being signed: a mark is coming. Above the ceiling nothing is coming
 * to this mode at all, so the ceiling answers ahead of every slot state and
 * reserves nothing — a box held there would promise a mark this order was
 * never going to be offered.
 *
 * In local dev (`import.meta.env.DEV`) the
 * platform gate is skipped so both wallet badges render side by side
 * regardless of which device is actually running the browser, letting the
 * pair be checked visually without switching hardware. Both signals are
 * populated only once a real `.pkpass` is signed and stored at fulfillment —
 * a pipeline emulator/seed data never runs — so dev also substitutes one
 * placeholder href when the real signal is empty, purely so the badge's own
 * layout is on screen to check; the placeholder link resolves to nothing and
 * is never present outside `import.meta.env.DEV`.
 *
 * `h-12` matches Google's own 48dp brand floor (google-wallet-badge.view.ts)
 * rather than the asset's own smaller native crop, so every action button on
 * order-access — icon-buttons included — shares one common height instead of
 * shrinking the wallet badges below either brand's mandated minimum.
 *
 * The anchor's own corner is `rounded-xl`, which is both the rung nearest the
 * corner Apple's artwork draws into itself at this height and the rung
 * `schmancy-button` already carries — so the mark and the text form it falls
 * back to read as one shape. Nothing clips the mark (no `overflow-hidden`, no
 * ground of its own), so that corner shapes the focus ring alone. Google's
 * badge sits on that same rung — reached by re-cornering its own artwork,
 * which is an override of Google's guidelines this deployment makes
 * deliberately (google-wallet-badge.view.ts) — so the two vendors' marks and
 * the buttons beside them read as one shape rather than three.
 *
 * The host is `display: contents` and the control is a direct child of
 * whatever row mounts it, with no wrapper of the badge's own: a wrapper is a
 * single flex item that a narrow row squeezes to one badge's width, folding
 * the rest onto new lines, so the row silently grows tall instead of
 * overflowing and the scroller it sits in never has anything to scroll. As
 * siblings, the controls are what the row measures, the row's own `gap` spaces
 * Apple's and Google's marks identically, and `shrink-0` holds each mark at
 * the fixed size both brands mandate — a row too narrow for all of them
 * overflows and scrolls rather than compressing them into each other.
 *
 * Either form is a real anchor to a signed artifact — a direct download, no
 * web service involved: Apple's device-facing register/unregister/serials/
 * fetch service only handles POST-ADD push updates, never the initial add.
 * order-access.api.ts resolves each stored Storage path to a bare alt=media
 * download URL before it lands here, the same seam ics/receipt resolve
 * through.
 *
 * Zero I/O, same as every packages/views/src role — this renders what
 * order-access.state.ts already holds and mutates nothing; the passes and
 * their bundle are signed and stored at fulfillment time, so unlike Google's
 * save link there is nothing to mint on the client.
 *
 * `ticketId`, when set, scopes the badge to that one ticket's own pass —
 * ticket-wallet-actions.view.ts's per-ticket overlay mounts it this way.
 * Left unset (the default), the order-level bundle renders, the mode the
 * header strip uses.
 */

import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
// Side-effect: registers <schmancy-skeleton>.
import "@mhmo91/schmancy/skeleton";
import { state } from "@mhmo91/schmancy/state";
import { css, html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { track$ } from "../../analytics/analytics.state.ts";
import { LocalizeMixin, uiLanguage } from "../../i18n/localize.ts";
import {
	walletAppleBundleUrl,
	walletAppleSlot,
	walletAppleUrls,
	walletBulkOffered,
} from "./order-access.state.ts";

/** Whether the shared badge asset failed to load — the choice between Apple's
 *  own mark and the plain text link at the same href, never between rendering
 *  and not. Module-scope rather than per-element: every instance points at the
 *  SAME file, so one failure is a fact about the deployment, not about one
 *  badge, and only a shared signal spares an overlay's freshly-mounted badge
 *  from flashing the broken image the first one already resolved. */
export const appleBadgeAssetBroken = state<boolean>(
	"storefront/order-access/apple-badge-asset-broken",
).memory(false);

/** Apple's own supplied SVG badge kit (45 locales), committed under
 *  web/public/wallet/ once the developer-site click-through is
 *  cleared — same tracked, same-origin, per-locale shape as
 *  add-to-google-wallet-{locale}.svg beside it.
 *
 *  Each entry carries the artwork's own intrinsic size, so `h-12 w-auto`
 *  resolves the box from the aspect ratio before the file itself arrives: an
 *  `<img>` with no dimensions is zero-wide until it loads, which is a shift
 *  inside the very state that exists to prevent one. The pair is each SVG's
 *  own viewBox at 1000x — the attributes take integers, and only their ratio
 *  is read once `w-auto` and `h-12` fix the used size. */
const BADGE: Readonly<
	Record<
		"en" | "de",
		{ readonly src: string; readonly width: number; readonly height: number }
	>
> = {
	en: {
		src: "/wallet/add-to-apple-wallet-en.svg",
		width: 110739,
		height: 35016,
	},
	de: {
		src: "/wallet/add-to-apple-wallet-de.svg",
		width: 110278,
		height: 34499,
	},
};

/** Whether this device's OS can open a `.pkpass` at all. The question is the
 *  operating system's, never the browser's or the form factor's: macOS opens a
 *  pass in Wallet from any browser, and iPadOS 13+ Safari — the default
 *  "Request Desktop Website" mode — sends a Macintosh user-agent, so an iPad
 *  answers true through that same term rather than needing one of its own. No
 *  web API reports pass support, so the user-agent is the only signal
 *  available; it is read for the OS name alone, which is the coarsest and
 *  therefore most durable thing a user-agent still tells the truth about. */
export function isApplePlatform(): boolean {
	return /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
}

/** Whether this session shows the Apple Wallet surface at all — the badge's
 *  own mark, the per-ticket trigger that opens one
 *  (ticket-wallet-actions.view.ts), and the line order-access.view.ts renders
 *  where a bundle was withheld. One term for all three: a trigger shown to a
 *  session the mark never reaches opens onto nothing, and an explanation shown
 *  there answers for a control that was never offered. The operating system
 *  decides (isApplePlatform above); dev overrides it so the row can be checked
 *  on any hardware. */
export function showsAppleWallet(): boolean {
	return isApplePlatform() || import.meta.env.DEV;
}

@schmancyElement("apple-wallet-badge")
export class AppleWalletBadgeView extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: contents;
			}
		`,
	];

	/** Scopes the badge to one ticket's own pass — null addresses the whole
	 *  order's bundle instead, the order-level mode. */
	@property({ attribute: false }) ticketId: string | null = null;

	override render(): TemplateResult {
		// The platform gate answers first, and it answers `never`: only Apple's
		// own operating systems open a `.pkpass`, so a reserved box here would
		// hold space on an Android phone for an action that can never arrive.
		if (!showsAppleWallet()) return html``;
		// The order-level mode's own ceiling, answered before the slot itself:
		// above it this mode is no offer rather than a smaller one, so nothing
		// is reserved and nothing is drawn. Per-ticket mode is untouched — one
		// ticket is one pass, which no ceiling reaches.
		if (this.ticketId === null && !walletBulkOffered.get()) return html``;
		// The mark's own footprint, held while the pass is being signed. The
		// reserved box is measured by mounting the same file the ready state
		// does and taking its intrinsic size, so the two paints agree to the
		// pixel across both locales with no width to keep in sync; its `@error`
		// carries the same asset verdict, so a deployment missing the kit
		// reaches the text form during the wait rather than after it. That text
		// form keeps its own visible wording, which is what holds its width
		// identical to the link it becomes — the wait is named in the
		// accessible name instead, which still contains the visible words.
		if (walletAppleSlot.get() === "pending") {
			const preparing = this.t(
				"storefront.orderAccess.walletAppleBadge.preparingAriaLabel",
			);
			return html`
				${when(
					appleBadgeAssetBroken.value,
					() => html`
						<schmancy-button
							variant="outlined"
							color="neutral"
							size="md"
							?disabled=${true}
							.busy=${true}
							aria-label=${preparing}
						>
							${this.t("storefront.orderAccess.walletAppleBadge.ariaLabel")}
						</schmancy-button>
					`,
					() => html`
						<span
							role="status"
							aria-busy="true"
							aria-disabled="true"
							aria-label=${preparing}
							class="relative inline-flex shrink-0 rounded-xl"
						>
							<img
								src=${BADGE[uiLanguage()].src}
								width=${BADGE[uiLanguage()].width}
								height=${BADGE[uiLanguage()].height}
								alt=""
								class="h-12 w-auto opacity-0"
								@error=${() => appleBadgeAssetBroken.set(true)}
							/>
							<schmancy-skeleton class="absolute inset-0 rounded-xl"></schmancy-skeleton>
						</span>
					`,
				)}
			`;
		}
		const passes = walletAppleUrls.get();
		const bundleUrl = walletAppleBundleUrl.get();
		// One control per mode, and one URL behind it: this ticket's own pass
		// when scoped, otherwise the whole order's `.pkpasses` bundle. The one
		// case that renders more than one control is an order fulfilled before
		// that bundle was written — it carries no bundle URL and only its
		// individual passes are addressable, so order level falls back to one
		// control per pass rather than rendering nothing.
		const realHrefs =
			this.ticketId != null
				? passes
						.filter((pass) => pass.ticketId === this.ticketId)
						.map((pass) => pass.url)
				: bundleUrl != null
					? [bundleUrl]
					: passes.map((pass) => pass.url);
		// Dev-only layout stand-in — see the file doc's own note above.
		// import.meta.env.VITEST excludes the unit-test run itself: those
		// assert the real "pending"/"never" slots render their own contract, a
		// shape the dev server's own visual stand-in must not shadow.
		const hrefs =
			realHrefs.length === 0 && import.meta.env.DEV && !import.meta.env.VITEST
				? (["#"] as const)
				: realHrefs;
		if (hrefs.length === 0) return html``;
		return html`
			${repeat(
				hrefs,
				(href) => href,
				(href, i) => {
					// One name, both forms: the mark carries it as the anchor's
					// accessible name, the text link renders it verbatim, so the
					// two are never separately worded controls for one action. The
					// indexed wording is reachable only from the multi-control
					// fallback above — a single control that adds everything is
					// never "1 of n".
					const name =
						hrefs.length > 1
							? this.t(
									"storefront.orderAccess.walletAppleBadge.ariaLabelIndexed",
									{ index: i + 1, count: hrefs.length },
								)
							: this.t("storefront.orderAccess.walletAppleBadge.ariaLabel");
					return when(
						appleBadgeAssetBroken.value,
						// `size="md"` is 48px — the same height Apple's own artwork
						// takes at `h-12`, so a deployment missing the file keeps the
						// actions row's geometry rather than shifting it. The button's
						// own host already declines to shrink, so a label as long as
						// the German one is never squeezed narrower than its words.
						() => html`
							<schmancy-button
								variant="outlined"
								color="neutral"
								size="md"
								href=${href}
								@click=${() =>
									track$.next({ name: "order_action", action: "wallet_apple" })}
							>
								${name}
							</schmancy-button>
						`,
						() => html`
							<a
								href=${href}
								aria-label=${name}
								class="inline-flex shrink-0 rounded-xl"
								@click=${() =>
									track$.next({ name: "order_action", action: "wallet_apple" })}
							>
								<img
									src=${BADGE[uiLanguage()].src}
									width=${BADGE[uiLanguage()].width}
									height=${BADGE[uiLanguage()].height}
									alt=""
									class="h-12 w-auto"
									@error=${() => appleBadgeAssetBroken.set(true)}
								/>
							</a>
						`,
					);
				},
			)}
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"apple-wallet-badge": AppleWalletBadgeView;
	}
}
